import React, { useRef, useEffect } from 'react';
import { TreeTable } from 'primereact/treetable';
import { Column } from 'primereact/column';
import styles from './EducationalTreeTable.module.css';

const REPORT_CATEGORIES = [
  { value: '', label: 'Все' },
  { value: 'Преподаватели', label: 'Преподаватели' },
  { value: 'Воспитатели', label: 'Воспитатели' },
  { value: 'Администрация', label: 'Администрация' },
];

const EducationalTreeTable = ({
  treeNodes,
  selectedNode,
  setSelectedNode,
  expandedKeys,
  setExpandedKeys,
  onDoubleClick,
  onAdd,
  onDelete,
  onEdit,
  onMoveUp,
  onMoveDown,
  onIndent,
  onOutdent,
  onExpandAll,
  onCollapseAll,
  canMoveUp,
  canMoveDown,
  canIndent,
  canOutdent,
  hasSelection,
  pending,
  reportType,
  reportCategory,
  onReportTypeChange,
  onReportCategoryChange,
  onRunReport,
}) => {
  const clickTimerRef = useRef(null);
  const lastClickNodeRef = useRef(null);

  useEffect(() => {
    return () => {
      if (clickTimerRef.current) clearTimeout(clickTimerRef.current);
    };
  }, []);

  const handleRowClick = (e) => {
    const currentNodeKey = e.node.key;

    if (e.originalEvent?.detail === 2) {
      if (clickTimerRef.current) {
        clearTimeout(clickTimerRef.current);
        clickTimerRef.current = null;
      }
      lastClickNodeRef.current = null;
      onDoubleClick?.(e.node.data);
      return;
    }

    if (lastClickNodeRef.current === currentNodeKey && clickTimerRef.current) {
      clearTimeout(clickTimerRef.current);
      clickTimerRef.current = null;
      lastClickNodeRef.current = null;
      onDoubleClick?.(e.node.data);
    } else {
      lastClickNodeRef.current = currentNodeKey;
      clickTimerRef.current = setTimeout(() => {
        clickTimerRef.current = null;
        lastClickNodeRef.current = null;
      }, 300);
    }
  };

  const nameBodyTemplate = (node) => {
    const hasChildren = node.children?.length > 0;
    return (
      <div className={styles.nameCell}>
        <span>{node.data.name}</span>
        {hasChildren && <span className={styles.childrenCount}>({node.children.length})</span>}
      </div>
    );
  };

  const teacherBodyTemplate = (node) => (
    <span className={styles.holderCell}>{node.data.fio || node.data.teacherName || ''}</span>
  );

  const infoBodyTemplate = (node) => (
    <span className={styles.infoCell} title={node.data.info || ''}>
      {node.data.info || ''}
    </span>
  );

  const getNodeDepth = (nodeKey, nodes, depth = 0) => {
    for (const node of nodes) {
      if (node.key === nodeKey) return depth;
      if (node.children?.length) {
        const found = getNodeDepth(nodeKey, node.children, depth + 1);
        if (found !== -1) return found;
      }
    }
    return -1;
  };

  const TBtn = ({ onClick, disabled, title, children }) => (
    <button
      type='button'
      className={styles.tbtn}
      onClick={onClick}
      disabled={disabled}
      title={title}
    >
      {children}
    </button>
  );

  return (
    <div className={styles.panel} style={{ position: 'relative' }}>
      {pending && (
        <div className={styles.loadingOverlay}>
          <div className={styles.loadingSpinner} />
        </div>
      )}
      {/* Toolbar */}
      <div className={styles.toolbar}>
        {/* Move order */}
        <TBtn onClick={onMoveUp} disabled={!canMoveUp} title='Переместить вверх'>
          <svg viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2'><polyline points='6 14 12 8 18 14'/><line x1='12' y1='8' x2='12' y2='18'/></svg>
        </TBtn>
        <TBtn onClick={onMoveDown} disabled={!canMoveDown} title='Переместить вниз'>
          <svg viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2'><polyline points='6 10 12 16 18 10'/><line x1='12' y1='6' x2='12' y2='16'/></svg>
        </TBtn>
        <TBtn onClick={onIndent} disabled={!canIndent} title='На уровень ниже'>
          <svg viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2'><line x1='4' y1='12' x2='14' y2='12'/><polyline points='11 8 15 12 11 16'/><line x1='20' y1='5' x2='20' y2='19'/></svg>
        </TBtn>
        <TBtn onClick={onOutdent} disabled={!canOutdent} title='На уровень выше'>
          <svg viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2'><line x1='20' y1='12' x2='10' y2='12'/><polyline points='13 8 9 12 13 16'/><line x1='4' y1='5' x2='4' y2='19'/></svg>
        </TBtn>

        <span className={styles.tsep} />

        {/* CRUD */}
        <TBtn onClick={onAdd} title='Добавить'>
          <svg viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2.2'><line x1='12' y1='5' x2='12' y2='19'/><line x1='5' y1='12' x2='19' y2='12'/></svg>
        </TBtn>
        <TBtn onClick={onDelete} disabled={!hasSelection} title='Удалить'>
          <svg viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2'><line x1='6' y1='6' x2='18' y2='18'/><line x1='18' y1='6' x2='6' y2='18'/></svg>
        </TBtn>
        <TBtn onClick={onEdit} disabled={!hasSelection} title='Изменить'>
          <svg viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2'><path d='M12 20h9'/><path d='M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4 12.5-12.5z'/></svg>
        </TBtn>

        <span className={styles.tsep} />

        {/* Report section */}
        <div className={styles.reportGroup}>
          <button type='button' className={styles.tbtnWide} title='Сформировать отчёт' onClick={onRunReport}>
            <svg viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2'><path d='M6 9V3h12v6'/><rect x='6' y='13' width='12' height='8'/><path d='M6 17H4a2 2 0 0 1-2-2v-2a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v2a2 2 0 0 1-2 2h-2'/></svg>
            Отчёт по
          </button>
          <select
            className={styles.tbSel}
            value={reportType}
            onChange={e => onReportTypeChange(e.target.value)}
          >
            <option value='class'>классу</option>
            <option value='subject'>предмету</option>
          </select>
          <select
            className={styles.tbSel}
            value={reportCategory}
            onChange={e => onReportCategoryChange(e.target.value)}
          >
            {REPORT_CATEGORIES.map(c => (
              <option key={c.value} value={c.value}>{c.label}</option>
            ))}
          </select>
        </div>

        <span className={styles.tspacer} />

        {/* Expand/collapse */}
        <TBtn onClick={onExpandAll} title='Развернуть всё'>
          <svg viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2'><polyline points='15 3 21 3 21 9'/><polyline points='9 21 3 21 3 15'/><line x1='21' y1='3' x2='14' y2='10'/><line x1='3' y1='21' x2='10' y2='14'/></svg>
        </TBtn>
        <TBtn onClick={onCollapseAll} title='Свернуть всё'>
          <svg viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2'><polyline points='4 14 10 14 10 20'/><polyline points='20 10 14 10 14 4'/><line x1='14' y1='10' x2='21' y2='3'/><line x1='3' y1='21' x2='10' y2='14'/></svg>
        </TBtn>
      </div>

      {/* Tree table */}
      <TreeTable
        value={treeNodes}
        selectionMode='single'
        selectionKeys={selectedNode}
        onSelectionChange={e => setSelectedNode(e.value)}
        expandedKeys={expandedKeys}
        onToggle={e => setExpandedKeys(e.value)}
        onRowClick={handleRowClick}
        scrollable
        scrollHeight='calc(100vh - 360px)'
        tableStyle={{ minWidth: '36rem' }}
        emptyMessage={<div style={{ textAlign: 'center', color: '#9aa7b8', padding: '2rem' }}>Нет данных</div>}
        className={styles.treeTable}
        rowClassName={(node) => {
          const depth = getNodeDepth(node.key, treeNodes);
          return {
            [styles.level0]: depth === 0,
            [styles.level1]: depth === 1,
            [styles.level2]: depth === 2,
            [styles.level3]: depth >= 3,
            [styles.selectedRow]: selectedNode && (
            typeof selectedNode === 'string'
              ? selectedNode === node.key
              : Object.keys(selectedNode)[0] === node.key
          ),
          };
        }}
      >
        <Column field='name' header='Название' body={nameBodyTemplate} expander style={{ width: '46%' }} />
        <Column field='fio' header='Должность занимает' body={teacherBodyTemplate} style={{ width: '30%' }} />
        <Column field='info' header='Описание' body={infoBodyTemplate} style={{ width: '24%' }} />
      </TreeTable>

      <div className={styles.tableHint}>
        Двойной клик по строке или кнопка «Изменить» — открыть карточку. Дерево поддерживает произвольную вложенность.
      </div>
    </div>
  );
};

export default EducationalTreeTable;
